/**
 * IN SHORT: This file handles Signing (Integrity). 
 * It adds a digital signature to your data to prove it came from you and hasn't been tampered with.
 *
 * JWS (JSON Web Signature) operations — RS256.
 *
 * SECURITY INVARIANTS:
 * 1. `alg` is always pinned to RS256. We pass it explicitly to both sign and
 *    verify; we never read it from the token header without cross-checking.
 * 2. On verify, we re-validate `exp`, `nbf`, `iss`, and `aud` ourselves in
 *    addition to what jose already checks, because the exact behaviour of
 *    jose's built-in validation differs across versions.
 * 3. `jwtVerify` requires a CryptoKey, not a JWK, so callers must have
 *    already imported the public key.
 */

import { SignJWT, jwtVerify } from 'jose';
import type { JWTVerifyOptions, JWTPayload } from 'jose';
import { JwtSdkError, JwtSdkErrorCode, AlgorithmMismatchError, TokenExpiredError } from '../errors/index.js';
import type { AppPayload } from '../types/index.js';
import { SIGNING_ALG } from '../types/index.js';
import { peekProtectedHeader } from '../utils/encoding.js';
import { nowSeconds, isExpired, isNotYetValid } from '../utils/time.js';

export interface SignOptions {
  payload: Record<string, unknown>;
  privateKey: CryptoKey;
  keyId: string;
  /** Unix epoch seconds. Defaults to nowSeconds() + tokenTtlSeconds. */
  expiresAt: number;
  issuer?: string;
  audience?: string;
  jti: string;
  nonce?: string;
}

export interface VerifyOptions {
  token: string;
  publicKey: CryptoKey;
  issuer?: string;
  audience?: string;
  clockSkewSeconds?: number;
}

export interface VerifyResult<T extends Record<string, unknown> = Record<string, unknown>> {
  payload: AppPayload<T>;
  protectedHeader: { alg: string; kid?: string; [k: string]: unknown };
}

/**
 * Sign a payload with RS256 and return a compact JWS string.
 */
export async function signJws(opts: SignOptions): Promise<string> {
  const now = nowSeconds();

  try {
    return await new SignJWT({
      ...opts.payload,
      nonce: opts.nonce,
    })
      .setProtectedHeader({ alg: SIGNING_ALG, kid: opts.keyId })
      .setIssuedAt(now)
      .setNotBefore(now)
      .setExpirationTime(opts.expiresAt)
      .setJti(opts.jti)
      .setIssuer(opts.issuer ?? 'nested-jwt-sdk-client')
      .setAudience(opts.audience ?? 'nested-jwt-sdk-server')
      .sign(opts.privateKey);
  } catch (err) {
    throw JwtSdkError.wrap(JwtSdkErrorCode.SIGNING_FAILED, 'JWS signing failed', err);
  }
}

/**
 * Verify a compact JWS string, enforce algorithm pinning, and return the payload.
 *
 * ALGORITHM CONFUSION PREVENTION:
 * We peek at the `alg` header before calling jwtVerify. If it is anything
 * other than RS256, we throw immediately. This prevents the "none" algorithm
 * attack and RSA→HMAC confusion attacks.
 */
export async function verifyJws<T extends Record<string, unknown> = Record<string, unknown>>(
  opts: VerifyOptions,
): Promise<VerifyResult<T>> {
  // Peek at header without verifying signature — only to check the algorithm.
  let headerAlg: string;
  try {
    const header = peekProtectedHeader(opts.token) as { alg?: string };
    headerAlg = header.alg ?? '';
  } catch {
    throw new JwtSdkError(JwtSdkErrorCode.INVALID_TOKEN, 'Malformed JWS token');
  }

  if (headerAlg !== SIGNING_ALG) {
    throw new AlgorithmMismatchError(SIGNING_ALG, headerAlg);
  }

  const verifyOptions: JWTVerifyOptions = {
    algorithms: [SIGNING_ALG], // Pin — jose will reject anything else.
    clockTolerance: opts.clockSkewSeconds ?? 30,
  };
  if (opts.issuer) verifyOptions.issuer = opts.issuer;
  if (opts.audience) verifyOptions.audience = opts.audience;

  let result: { payload: JWTPayload; protectedHeader: { alg: string; kid?: string } };
  try {
    result = await jwtVerify(opts.token, opts.publicKey, verifyOptions) as typeof result;
  } catch (err) {
    // jose v6 sets a `code` property on its error objects. Use that rather than
    // parsing the message string, which can change across releases.
    const code = (err as Record<string, unknown>)['code'];
    if (code === 'ERR_JWT_EXPIRED') throw new TokenExpiredError();
    throw JwtSdkError.wrap(JwtSdkErrorCode.VERIFICATION_FAILED, 'JWS verification failed', err);
  }

  // Belt-and-suspenders: manually recheck `exp` and `nbf`.
  const p = result.payload as AppPayload;
  const skew = opts.clockSkewSeconds ?? 30;

  if (p.exp !== undefined && isExpired(p.exp, skew)) {
    throw new TokenExpiredError();
  }
  if (p.nbf !== undefined && isNotYetValid(p.nbf, skew)) {
    throw new JwtSdkError(JwtSdkErrorCode.INVALID_CLAIMS, 'Token is not yet valid (nbf)');
  }

  return {
    payload: p as AppPayload<T>,
    protectedHeader: result.protectedHeader,
  };
}
